import { Button, Col, DesignTokens, EditIcon, InfoIcon, Row, Table, TableActionBarRow, Text, Title, ValueOrFallback } from "melco-ui";
import { useTranslation } from "react-i18next";
import {
  FormattedDate,
  MelcoModelsUserMachineLicenseListItem,
} from "melco-shared-logic";
import { useMachineLicenses } from "../../../hooks/infrastructure/machinelicenses/useMachineLicenses";
import { StatusColorMap } from "../../../types/status";
import { useMachineLicensesOverview } from "../../../hooks/infrastructure/machinelicenses/useMachineLicensesOverview";
import dayjs from "dayjs";
import styled from "styled-components";
import { formatUTCDate } from "../../../helper/date/dateHelper";
import { ManageMachineLicenseDrawer } from "./ManageMachineLicenseDrawer";
import { useState } from "react";
import { AsyncTooltip, TooltipBulletItem, TooltipContainer, TooltipText, TooltipTitle } from "../../tootltip/AsyncTooltip";
import { useAccountGetPendingTransaction } from "../../../hooks/infrastructure/machinelicenses/useAccountGetPendingTransaction";
import { capitalize } from "../../../helper/common/util";

const DETAIL_VIEW_PATH = "/infrastructure/licenses";

const { spacings } = DesignTokens;


const CellWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`;

const TooltipWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
`;

export const MachineLicensesList: React.FC = () => {
  const {
    isLoading,
    isFetching,
    data,
    isPreviousData,
    errorComponent,
    onTableConfigChange,
    pagination,
    searchQueryChange,
    refetch: machineLicensesRefetch
  } = useMachineLicenses();

  const {

    data: machineLicensesOverview,
    refetch: machineLicensesOverviewRefetch
  } = useMachineLicensesOverview();
  console.log("machineLicensesOverview ", JSON.stringify(machineLicensesOverview))
  const { t } = useTranslation();

  //const accountGetPendingTransaction = useAccountGetPendingTransaction();

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [activeTooltipId, setActiveTooltipId] = useState<string | null>(null);

  const handleToggle = (id: string | null) => {
    if (activeTooltipId === id) {
      // Clicked the same icon → close it
      setActiveTooltipId(null);
    } else {
      // Clicked another icon  open the new one

      setTimeout(() => {
        setActiveTooltipId(id);
      }, 500);
    }
  };

  const callbackFn = () => {
    machineLicensesOverviewRefetch();
    machineLicensesRefetch()
    setDrawerOpen(false)
  }
  const displayExpiry = (item: MelcoModelsUserMachineLicenseListItem) => {
    const now = dayjs();
    if (item.is_subscription === false) {
      return t("machineLicenses.does_not_expire");
    }

    if (item.is_subscription === true) {
      if (!item.cancelled_on) {
        return t("machineLicenses.does_not_expire");
      } else {
        if (dayjs(item.cancelled_on).isBefore(now)) {
          return t("machineLicenses.expired");
        } else {
          if (item.cancelled_on) {
            return formatUTCDate(item.cancelled_on, "L");
          }

        }
      }
    }

    /**
     *   It is fallback so that if anyhow is_subscription is not
      * coming in response , just to avoid run time exception / error
      * we will display Does not expire
     */

    return t("machineLicenses.does_not_expire");
  };

  const LicenseStatusCell = ({ item }: { item: MelcoModelsUserMachineLicenseListItem }) => {
    const { t } = useTranslation();
    const status = item?.status ?? -1;
    const color = StatusColorMap[status];
    const label = t(`machineLicenses.licenseStatus.${status}`, '');

    return <CellWrapper><Text type={color}>{label}</Text>

      {
        status == 2 && (<TooltipWrapper><AsyncTooltip
          id={item.transaction_id ?? ""}
          key={item.transaction_id ?? ""}
          param={item.transaction_id ?? ""}
          isActive={activeTooltipId === item.transaction_id}
          onToggle={handleToggle}
          useDataHook={useAccountGetPendingTransaction}
          renderContent={(data) => {
            if (!data) return <TooltipText>{t("infrastructureLicenses.tooltip.no_data_available")}</TooltipText>;

            const date = formatUTCDate(data.subscription_start_date, "YYYY-MM-DD");
            const old_sub_type = capitalize(data?.old_sub_type ?? "");
            const new_sub_type = capitalize(data?.new_sub_type ?? "");
            const old_license_count = data.old_license_count
            const new_license_count = data.new_license_count

            return (
              <TooltipContainer>
                <TooltipTitle>{t("machineLicenses.tooltip.changes_apply", { date })}</TooltipTitle>
                {old_license_count && new_license_count && <TooltipBulletItem>
                  {t("machineLicenses.tooltip.licenses_count", { old_license_count, new_license_count })}
                </TooltipBulletItem>}
                {old_sub_type && new_sub_type && <TooltipBulletItem>
                  {t("machineLicenses.tooltip.billing_change", { old_sub_type, new_sub_type })}
                </TooltipBulletItem>}
              </TooltipContainer>
            );
          }}
        />
        </TooltipWrapper>)

      }</CellWrapper>;
  };


  const columns = [
    {
      title: t("machineLicenses.list.columns.transaction_id"),
      key: "transaction_id",
      dataIndex: "transaction_id",
      sorter: true,

      width: 150
    },
    {
      title: t("machineLicenses.list.columns.amount"),
      key: "license_count",
      dataIndex: "license_count",
      sorter: true,
      width: 90
    },
    // {
    //   title: t("machineLicenses.list.columns.license_type"),
    //   key: "is_subscription",
    //   // dataIndex: "product_version_number",
    //   sorter: true,
    //   width: 80
    //   , render: (_: string, item: MelcoModelsUserMachineLicenseListItem) => {
    //     return <>
    //       {
    //         item.is_subscription ? t("machineLicenses.list.subscription")
    //           :
    //           t("machineLicenses.list.one_time_buy")
    //       }
    //     </>
    //   }
    // },
    {
      title: t("machineLicenses.list.columns.level"),
      key: "level",
      //  dataIndex: "level",
      sorter: true,
      render: (_: string, item: MelcoModelsUserMachineLicenseListItem) => (<>{t(`machineLicenses.licenseLevel.${item.level?.toLowerCase()}`, '')}</>),
      width: 90
    },
    {
      title: t("machineLicenses.list.columns.user_name"),

      key: "user_name",
      dataIndex: "user_name",
      sorter: true,
      width: 130
    },
    {
      title: t("machineLicenses.list.columns.created"),
      key: "created_date",
      dataIndex: "created_date",
      sorter: true,
      width: 110,
      render: (_: string, item: MelcoModelsUserMachineLicenseListItem) => (
        <FormattedDate date={item.created_date} />
      ),
    },
    {
      title: t("machineLicenses.list.columns.subscription_start_date"),
      key: "subscription_start_date",
      dataIndex: "subscription_start_date",
      sorter: true,
      width: 110,
      render: (_: string, item: MelcoModelsUserMachineLicenseListItem) => (
        <>{item.subscription_start_date ? formatUTCDate(item.subscription_start_date, "L") : ""}</>
      ),
    },
    {
      title: t("machineLicenses.list.columns.renews_on"),
      key: "renewal_date",
      dataIndex: "renewal_date",
      sorter: true,
      width: 110,
      render: (_: string, item: MelcoModelsUserMachineLicenseListItem) => (
        <> {
          item.is_subscription
            ? (
              item.cancelled_on ? "" : (item.renewal_date
                ? formatUTCDate(item.renewal_date, "L")
                : "")
            )
            : t("machineLicenses.no_renewal")
        }
        </>
      ),
    },
    {
      title: t("machineLicenses.list.columns.expires_on"),
      key: "expiration_date",
      dataIndex: "expiration_date",
      sorter: true,
      width: 110,
      render: (_: string, item: MelcoModelsUserMachineLicenseListItem) => (
        displayExpiry(item)
      ),
    },

    {
      title: t("machineLicenses.list.columns.status"),
      key: "status",
      //dataIndex: "status",
      sorter: true,
      width: 130,
      render: (_: string, item: MelcoModelsUserMachineLicenseListItem) => (
        <LicenseStatusCell item={item} />
      ),

    }];


  return (
    <>
      <TableActionBarRow>
        <Col xs={24} xl={17}>
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Title level={4}>{t("machineLicenses.overview.title")}</Title>
            </Col>
          </Row>

          <Row gutter={[spacings.middle, spacings.middle]}>
            <Col xs={24} md={6}>
              <div>
                <Text strong>{t("machineLicenses.overview.total_licenses")}</Text>
                <div>
                  <ValueOrFallback
                    value={machineLicensesOverview?.total_licenses ?? ""}
                    fallback={t("global.fallback.none")}
                  />

                </div>
              </div>
            </Col>

            <Col xs={24} md={6}>
              <div>
                <Text strong>{t("machineLicenses.overview.assigned_licenses")}</Text>
                <div>
                  <ValueOrFallback
                    value={machineLicensesOverview?.assigned_licenses ?? ""}
                    fallback={t("global.fallback.none")} />

                </div>
              </div>
            </Col>

            <Col xs={24} md={6}>
              <div>
                <Text strong>{t("machineLicenses.overview.available_licenses")}</Text>
                <div>
                  <ValueOrFallback
                    value={machineLicensesOverview?.available_licenses ?? ""}
                    fallback={t("global.fallback.none")}
                  />
                </div>
              </div>
            </Col>

            <Col xs={24} md={6}>
              <div>
                <Text strong>{t("machineLicenses.overview.license_assignment")}</Text>
                <div style={{ marginTop: "5px" }}>
                  <Button
                    type="primary"
                    onClick={() => setDrawerOpen(true)}
                    icon={<EditIcon />}
                  >
                    {t("machineLicenses.actions.manage.button")}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Col>
      </TableActionBarRow>
      <TableActionBarRow>
        <Col xs={24} xl={17}>
          <Title level={4}>{t("machineLicenses.transaction_history")}</Title>
        </Col>
      </TableActionBarRow>
      <Table
        showSorterTooltip={false}
        loading={isPreviousData ? isFetching : isLoading}
        dataSource={data}
        sticky
        columns={columns}
        onChange={onTableConfigChange}
        pagination={pagination}
        rowKey="id"
        //onRow={onRow}
        noDataMessage={t("machineLicenses.list.empty")}
        errorComponent={errorComponent}
      />
      {isDrawerOpen && <ManageMachineLicenseDrawer
        isDrawerOpen={isDrawerOpen}
        setDrawerOpen={setDrawerOpen}
        callbackFn={callbackFn}
      />}
    </>
  );
};
